import React, { useState } from 'react';

import { DialogActions, Typography } from '@mui/material';
import type { GridApi } from '@mui/x-data-grid-premium';
import { gridFilterModelSelector } from '@mui/x-data-grid-premium';
import { difference } from 'ramda';
import {
  AlineAlert,
  AlineButton,
  AlineDialog,
  ConfirmationDialog,
  ContractTypeAutocomplete,
} from 'src/components';
import type { ContractTypeOption } from 'src/components';
import { useIsOrgAdmin } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import { getSelectedGroup, useCreateContractTypes, useNotificationDispatch } from 'src/hooks';
import { NotificationsActionType } from 'src/notifications';
import { makeStyles } from 'tss-react/mui';
import { v4 as uuid4 } from 'uuid';

const useStyles = makeStyles()(theme => ({
  alert: {
    marginBottom: theme.spacing(1),
  },
  changeTypeButton: {
    marginLeft: theme.spacing(1),
  },
  inputLabel: {
    ...theme.appTypography.fieldLabel,
    marginBottom: theme.spacing(1),
  },
  paper: {
    width: 566,
  },
}));

interface ChangeContractTypeDialogProps {
  apiRef: React.RefObject<GridApi | null>;
  onClose: () => void;
  open: boolean;
  selectedDocumentIds: UUID[];
  shouldRunForAllDocuments: boolean;
  totalDocumentCount: number;
  reportId?: UUID;
}

export function ChangeContractTypeDialog({
  apiRef,
  onClose,
  open,
  reportId,
  selectedDocumentIds,
  shouldRunForAllDocuments = false,
  totalDocumentCount,
}: ChangeContractTypeDialogProps): JSX.Element {
  const { classes } = useStyles();
  const dispatch = useNotificationDispatch();
  const { createContractTypes } = useCreateContractTypes();
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [type, setType] = useState<ContractTypeOption | null>(null);
  const [changeDocumentsType] = Apollo.useChangeDocumentsTypeMutation();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState<boolean>(false);
  const autocompleteValue: ContractTypeOption[] = type ? [type] : [];

  async function findOrCreateTypeId(): Promise<UUID | null> {
    if (type?.__typename === 'NewContractType') {
      const { data } = await createContractTypes({ variables: { input: [{ value: type.value }] } });
      return data?.createContractTypes.at(0)?.id ?? null;
    }

    return type?.id ?? null;
  }

  async function changeContractType(): Promise<void> {
    // v9 useGridApiRef returns RefObject<X | null>; gridFilterModelSelector
    // reads from apiRef.current internally so guard against the pre-mount case.
    if (!apiRef.current) return;
    setSubmitting(true);

    const typeId = await findOrCreateTypeId();
    const filterModel = gridFilterModelSelector(apiRef);
    const selectedGroup = getSelectedGroup(apiRef);
    const runAllDocs = Boolean(selectedGroup) || shouldRunForAllDocuments;
    const { data } = await changeDocumentsType({
      variables: {
        input: {
          documentIds: runAllDocs ? [] : selectedDocumentIds,
          filterModel,
          groupSelection: selectedGroup?.groupSelection ?? null,
          reportId,
          runAllDocs,
          typeId,
        },
      },
    });
    const numSuccessString = `Changed contract type for ${data?.changeDocumentsType.numSuccess} documents.`;

    if (data?.changeDocumentsType) {
      dispatch({
        payload: {
          autoHideDuration: 15000,
          key: uuid4(),
          message: data?.changeDocumentsType.numSkipped
            ? numSuccessString + ` ${data?.changeDocumentsType.numSkipped} documents were skipped.`
            : numSuccessString,
          variant: 'success',
        },
        type: NotificationsActionType.PUSH_NOTIFICATION,
      });
      apiRef.current?.setRowSelectionModel({ ids: new Set(), type: 'include' });
    }

    setSubmitting(false);
    onClose();
  }

  return (
    <AlineDialog
      TransitionProps={{ onExit: () => setType(null) }}
      classes={{ paper: classes.paper }}
      open={open}
      title="Change contract type?"
      onClose={onClose}
    >
      <UpdateDocumentsAuthorizationAlert />
      <Typography noWrap className={classes.inputLabel}>
        Contract Type
      </Typography>
      <ContractTypeAutocomplete
        suggestNewOption
        value={autocompleteValue}
        onChange={(_e, contractTypes) => {
          const newContractType = difference(contractTypes, autocompleteValue).at(0);
          setType(newContractType ?? null);
        }}
      />
      <DialogActions>
        <AlineButton color="primary" variant="outlined" onClick={onClose}>
          Cancel
        </AlineButton>
        <AlineButton
          className={classes.changeTypeButton}
          color="primary"
          disabled={confirmDialogOpen}
          loading={submitting && !confirmDialogOpen}
          variant="contained"
          onClick={async () => {
            if (shouldRunForAllDocuments) return setConfirmDialogOpen(true);
            await changeContractType();
          }}
        >
          {type
            ? type.__typename === 'NewContractType'
              ? 'Create Type'
              : 'Change Type'
            : 'Remove Type'}
        </AlineButton>
      </DialogActions>
      <ConfirmationDialog
        message={createUpdateAllDocsMsg(
          `change the contract type to "${type?.value}"`,
          totalDocumentCount
        )}
        open={confirmDialogOpen}
        primaryText="Yes"
        secondaryText="No"
        title={`Change contract type for all documents?`}
        onClickPrimary={changeContractType}
        onClose={() => setConfirmDialogOpen(false)}
      />
    </AlineDialog>
  );
}

export function createUpdateAllDocsMsg(action: string, totalDocumentCount: number): string {
  return `Please confirm you want to ${action} for all ${totalDocumentCount} documents. This action will apply to ALL documents that match your current filters, not just the documents in view.`;
}

export function UpdateDocumentsAuthorizationAlert(): JSX.Element | null {
  const { classes } = useStyles();
  const { isOrgAdmin } = useIsOrgAdmin();

  if (isOrgAdmin) return null;

  return (
    <AlineAlert className={classes.alert} severity="info">
      Only documents that you are an Editor or Owner of will be updated. Contact an Admin to update
      other documents.
    </AlineAlert>
  );
}
